import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cn } from 'lib/cn';
import { CheckIcon, AlertIcon, InfoIcon, CloseIcon } from 'components/icons';

const ToastContext = createContext(null);

let nextId = 0;

/** Accent colour + icon per tone (inline-styled so the accent is dynamic). */
const TONES = {
  success: { accent: '#10b981', Icon: CheckIcon },
  danger: { accent: '#ef4444', Icon: AlertIcon },
  warning: { accent: '#f59e0b', Icon: AlertIcon },
  info: { accent: '#0ea5e9', Icon: InfoIcon },
};

/** How long the exit animation runs before the toast is unmounted. */
const EXIT_MS = 220;

/**
 * App-wide toast notifications — a web port of the mobile toast: a frosted card
 * that slides up from the bottom with a coloured left accent + indicator bar.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Saved', 'Your product was added.');
 *   toast.error('Failed', err.message);
 *   toast.info('Heads up', 'New order', { actionText: 'View', onAction: goToOrder });
 *   toast.warning('Low stock', 'Almonds running low', { persistent: true });
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((tone, title, message, options = {}) => {
    const id = ++nextId;
    const { duration = 4000, persistent = false, actionText, onAction } = options;
    setToasts((current) => [
      ...current,
      { id, tone, title, message, duration, persistent, actionText, onAction },
    ]);
    return id;
  }, []);

  const api = useMemo(
    () => ({
      success: (title, message, options) => push('success', title, message, options),
      error: (title, message, options) => push('danger', title, message, options),
      warning: (title, message, options) => push('warning', title, message, options),
      info: (title, message, options) => push('info', title, message, options),
      show: (tone, title, message, options) =>
        push(TONES[tone] ? tone : 'info', title, message, options),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 pb-6">
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onRemove={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onRemove }) {
  const { id, tone, title, message, duration, persistent, actionText, onAction } = toast;
  const { accent, Icon } = TONES[tone] || TONES.info;
  const [visible, setVisible] = useState(false);

  // Enter animation on mount.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Play the exit animation, then unmount.
  const beginClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => onRemove(id), EXIT_MS);
  }, [id, onRemove]);

  // Auto-dismiss unless persistent.
  useEffect(() => {
    if (persistent || !duration) return undefined;
    const timer = setTimeout(beginClose, duration);
    return () => clearTimeout(timer);
  }, [persistent, duration, beginClose]);

  const handleActivate = () => {
    if (onAction) onAction();
    beginClose();
  };

  return (
    <div
      className={cn(
        'pointer-events-auto w-full max-w-md transform transition-all duration-200 ease-out',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
      )}
    >
      <div
        className="flex items-center gap-3 rounded-xl border border-white/40 bg-white/95 p-4 shadow-lg backdrop-blur"
        style={{ borderLeftColor: accent, borderLeftWidth: 4 }}
      >
        <button
          type="button"
          onClick={handleActivate}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span className="shrink-0" style={{ color: accent }}>
            <Icon className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            {title && <span className="block text-sm font-bold text-slate-900">{title}</span>}
            {message && (
              <span className="block break-words text-sm text-slate-600">{message}</span>
            )}
            {onAction && (
              <span className="mt-1 block text-xs font-bold" style={{ color: accent }}>
                {actionText || 'Tap to learn more'}
              </span>
            )}
          </span>
        </button>

        <span className="h-9 w-1 shrink-0 rounded-full" style={{ backgroundColor: accent }} />

        {persistent && (
          <button
            type="button"
            onClick={beginClose}
            className="shrink-0 rounded-full p-1 text-slate-400 transition hover:bg-black/5 hover:text-slate-600"
            aria-label="Dismiss notification"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a <ToastProvider>.');
  }
  return context;
}

export default ToastProvider;
